/* eslint-disable react-hooks/exhaustive-deps */
import FormFooter from "components/FormFooter/FormFooter";
import React, { useContext, useEffect, useState } from "react";
import { AuthenticationContext, CustomerPermission } from "services/context/authentication.context";
import { toast } from 'react-toastify';
import { useHistory } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { ConfirmAgreementOfferRejectModal } from "components/electricity/NewElectricityAgreement/modal/ConfirmAgreementOfferRejectModal";
import { PageProps } from "models/page";
import { ChargingAgreementStatus } from "models/charging/agreement";
import ChargingAgreementService from "services/charging/charging-agreement.service";
import { isEmailValidAndPresent } from "helpers/validators";
import moment from "moment";
import { isoDateFormat } from "common/dateFormatterConfig";
import NewChargingAgreementConfirmation from "components/charging/NewChargingAgreement/NewChargingAgreementConfirmation";
import { NewChargingAgreementSummary } from "components/charging/NewChargingAgreement/NewChargingAgreementSummary";

export interface ChargingAgreementForm {
  customerName?: string;
  registryNumber?: string;
  vatRegistryNumber?: string;
  phone?: string;
  email?: string;
  address?: string; 
  city?: string;
  zip?: string;
  county?: string;
  startDate?: Date;
  validated: boolean;
}

export enum NewChargingAgreementState {
  CONFIRM,
  COMPLETED
}

export function NewChargingAgreement(props: PageProps) {
    const [currentState, setCurrentState] = useState(NewChargingAgreementState.CONFIRM);
    const [agreementTermsAccepted, setAgreementTermsAccepted] = useState(false);
    const [rejectOfferModalVisible, setRejectOfferModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState<ChargingAgreementForm>({
        validated: false
    });

    const { state, dispatch, hasPermission } = useContext(AuthenticationContext);
    let history = useHistory();

    useEffect(() => {
        props.onLoadValidatePage();

        return () => {
          dispatch({
            type: "setViewOffer",
            value: null
          });
        }
    }, []);

    useEffect(() => {
      const agreement = state.viewChargingAgreementOffer;

      if(!agreement) return;

      setForm({
          ...form,
          customerName: agreement.customer.name,
          registryNumber: agreement.customer.registryNumber,
          email: agreement.contactPerson.email,
          phone: agreement.contactPerson.phone,
          address: !agreement.customer.city ? undefined : agreement.customer.address,
          city: agreement.customer.city,
          zip: agreement.customer.zip,
          county: agreement.customer.county,
      });
  }, [state.viewChargingAgreementOffer]);

    useEffect(() => {
        if (state.customer && !hasPermission(CustomerPermission.WEB_ADMIN)) history.push("/");

        setForm({
            ...form,
            customerName: state.customer?.name,
            registryNumber: state.customer?.registryNumber,
            vatRegistryNumber: state.customer?.vatRegistryNumber,
            email: state.customer?.email,
            phone: state.customer?.phone,
            address: !state.customer?.city ? undefined : state.customer?.address,
            city: state.customer?.city,
            zip: state.customer?.zip,
            county: state.customer?.county,
            startDate: undefined
        });
    }, [state.customer]);

    useEffect(() => {
      if (state.customerId) {
        getPendingAgreements(state.customerId);
      }
    }, []);
  
    const getPendingAgreements = async (customerId: number) => {
      try {
        const res = await ChargingAgreementService.getAgreements(customerId);
  
        dispatch({
          type: "setViewChargingOffer",
          value: res.filter(agr => agr.status === ChargingAgreementStatus.PENDING)[0]
        })
      } catch (e) {
        console.error(e);
      }
    };

    if (!state.customer || (!state.viewChargingAgreementOffer && currentState === NewChargingAgreementState.CONFIRM)) {
        return (
            <div className='d-flex justify-content-center mt-5'>
                <Spinner animation="grow" variant="primary" />
            </div>
        );
    }
    
    const validateForm = () => {
      setForm({ ...form, validated: true });
      if (!isEmailValidAndPresent(form.email)) return false;
      if (!form.phone?.trim().length) return false;
      if (!form.address) return false;
      return true;
    };

    const onRejectOffer = async () => {
        try{
          await ChargingAgreementService.rejectAgreementOffer(state.viewChargingAgreementOffer!.id, state.customerId!, state.customer!.contactId)
          setRejectOfferModalVisible(false);
          toast('Pakkumusest keeldutud', { type: 'warning' });
          history.replace('/lepingud');
        }catch(e){
          console.error(e);
        }
    }

    const onAcceptOffer = async () => {
      if(!state.customer || !validateForm()) return;
      
      try{
        setLoading(true);

        await ChargingAgreementService.acceptAgreementOffer(
          state.viewChargingAgreementOffer!.id,
          state.customerId!,
          {
            startDate: moment(form.startDate!).startOf('day').add(2, 'hours').format(isoDateFormat),
            email: form.email!,
            phone: form.phone!,
            address: form.address!,
            city: form.city,
            zip: form.zip,
            county: form.county
          },
          state.customer!.contactId
        )

        dispatch({
          type: "setCustomer",
          value: {
              ...state.customer,
              phone: form.phone,
              email: form.email,
              address: form.address,
              city: form.city,
              zip: form.zip,
              county: form.county
          }
        });

        dispatch({
          type: "setViewChargingOffer",
          value: null
        })

        setCurrentState(NewChargingAgreementState.COMPLETED)
      }catch(e){
        console.error(e);
      }finally{
        setLoading(false);
      }
    }

    return (
        <div className="new-charging-agreement">
            {
              currentState === NewChargingAgreementState.CONFIRM ? (
                <NewChargingAgreementSummary
                    agreement={state.viewChargingAgreementOffer!}
                    isCompany={state.customer && !state.customer.isPerson && !state.customer.apartmentAssociation}
                    form={form}
                    setForm={setForm}
                    termsAccepted={agreementTermsAccepted}
                    toggleTerms={() => setAgreementTermsAccepted(!agreementTermsAccepted)}
                />   
              ) : null
            }
            {
              currentState === NewChargingAgreementState.COMPLETED ? (
                <NewChargingAgreementConfirmation/>   
              ) : null
            }
            {
              hasPermission(CustomerPermission.WEB_ADMIN) && (
                 <FormFooter
                    previous={() => onRejectOffer()}
                    next={() => onAcceptOffer()}
                    nextButtonDisabled={false}
                    loading={loading}
                    showPrevious={true}
                    showNext={true}
                    previousLabel={"Loobu"}
                    nextLabel={"Sõlmi leping"}
                ></FormFooter>
              )
            }
            {rejectOfferModalVisible ? <ConfirmAgreementOfferRejectModal 
                confirm={onRejectOffer}
                cancel={() => setRejectOfferModalVisible(false)}
            /> : null}
        </div>
    )
}